import { EmotionalFamily, EmotionalState, statesOfFamily } from '../../../domain/emotion/enums';
import type {
  EmotionalFamilyPresentationDefinition,
  EmotionalStatePresentationDefinition,
  MovementIntensity,
  PresentationElement,
} from './presentation-types';

/**
 * ASCEND V7 — présentation des 8 familles et 36 états, réconciliée avec le domaine
 * `domain/emotion/enums.ts` (seule source de vérité pour la relation état → famille, jamais
 * dupliquée ici). Un état ne porte que son libellé ; toute la présentation (image, accent,
 * icône, lumière, ton, pensée, intention, routines) vient de sa famille — voir
 * `resolvePresentation.ts`.
 */

/**
 * Point focal `object-position` par image de famille — pack V2 (1870×841, ratio ~2.22:1), même
 * exigence que pour les modes : chaque photo a une composition propre, jamais un centrage
 * uniforme. Valeurs déterminées par inspection visuelle directe de chaque fichier V2 — voir
 * revue visuelle « intégration finale des 20 visuels » de la mission V7.
 */
const HERO_IMAGE_POSITION: Record<string, string> = {
  rayonnement: '42% 55%',
  serenite: '45% 50%',
  'pression-peur': '50% 55%',
  friction: '78% 55%',
  vulnerabilite: '35% 55%',
  reparation: '42% 55%',
  recentrage: '50% 55%',
  'coup-dur': '30% 60%',
};
const DEFAULT_HERO_IMAGE_POSITION = '50% 50%';

function family(
  domainFamily: EmotionalFamily,
  key: string,
  label: string,
  icon: EmotionalFamilyPresentationDefinition['icon'],
  accent: string,
  light: string,
  tone: string,
  movementIntensity: MovementIntensity,
  intention: string,
  thought: string,
  routines: readonly string[],
  attenuates: readonly PresentationElement[],
): EmotionalFamilyPresentationDefinition {
  return {
    family: domainFamily,
    key,
    labelKey: `ascend.family.${key}.label`,
    label,
    icon,
    scopeClass: `family-${key}`,
    heroImageKey: `family-${key}-v2.png`,
    heroImagePosition: HERO_IMAGE_POSITION[key] ?? DEFAULT_HERO_IMAGE_POSITION,
    accent,
    light,
    tone,
    movementIntensity,
    intentionKey: `ascend.family.${key}.intention`,
    intention,
    thoughtKey: `ascend.family.${key}.thought`,
    thought,
    routineKeys: routines.map((_, index) => `ascend.family.${key}.routine.${index}`),
    routines,
    attenuates,
  };
}

export const EMOTIONAL_FAMILY_PRESENTATIONS = {
  [EmotionalFamily.rayonnement]: family(
    EmotionalFamily.rayonnement,
    'rayonnement',
    'RAYONNEMENT',
    'rayonnement',
    '#D4AF37',
    'haute, dorée, rasante',
    'ouvert, sobre, sans exubérance',
    'normal',
    "Accueillir l'élan sans perdre l'axe.",
    '« Ce qui va bien mérite la même attention que ce qui résiste. »',
    ['Commencer avec intention', "Réaliser l'action essentielle", 'Terminer avec clarté'],
    [],
  ),
  [EmotionalFamily.serenite]: family(
    EmotionalFamily.serenite,
    'serenite',
    'SÉRÉNITÉ',
    'serenite',
    '#7FA6B5',
    'égale, froide, sans ombre portée',
    'calme, posé, descriptif',
    'normal',
    'Tenir ce qui est juste, sans le forcer.',
    "« Le calme n'est pas l'absence de mouvement, c'est la justesse du rythme. »",
    ['Commencer sans hâte', "Réaliser l'action essentielle", 'Clore la journée en silence'],
    [],
  ),
  [EmotionalFamily.pressionPeur]: family(
    EmotionalFamily.pressionPeur,
    'pression-peur',
    'PRESSION ET PEUR',
    'pression-peur',
    '#B87333',
    'resserrée, contrastée',
    'bref, factuel, non injonctif',
    'normal',
    'Réduire le champ à ce qui dépend de toi.',
    '« La peur rétrécit le champ. Une seule chose à la fois le rouvre. »',
    ['Nommer ce qui inquiète', 'Faire la seule action qui dépend de toi', 'Poser la journée avant la nuit'],
    ['editorial-cards'],
  ),
  [EmotionalFamily.friction]: family(
    EmotionalFamily.friction,
    'friction',
    'FRICTION',
    'friction',
    '#A8524C',
    'basse, mate',
    'neutre, sans jugement, sans arbitrage',
    'reduced',
    'Laisser retomber avant de trancher.',
    '« La colère renseigne sur ce qui compte. Elle ne décide pas. »',
    ['Différer la réaction', "Réaliser l'action essentielle", 'Reprendre le sujet à froid'],
    ['priorities'],
  ),
  [EmotionalFamily.vulnerabilite]: family(
    EmotionalFamily.vulnerabilite,
    'vulnerabilite',
    'VULNÉRABILITÉ',
    'vulnerabilite',
    '#8A8170',
    'diffuse, très basse',
    'doux, permissif, jamais culpabilisant',
    'reduced',
    'Traverser sans se juger.',
    '« Ce que tu vaux ne se mesure pas à la journée que tu viens de vivre. »',
    ['Commencer petit', 'Faire une seule chose utile', 'Terminer sans bilan'],
    ['editorial-cards', 'priorities'],
  ),
  [EmotionalFamily.reparation]: family(
    EmotionalFamily.reparation,
    'reparation',
    'RÉPARATION',
    'reparation',
    '#7E9B8E',
    'voilée, régénérative',
    'encourageant sans exiger',
    'reduced',
    'Réparer sans forcer.',
    '« Se reposer est aussi un acte de discipline. »',
    ['Se lever sans injonction', 'Bouger doucement', 'Se coucher plus tôt'],
    ['priorities'],
  ),
  [EmotionalFamily.recentrage]: family(
    EmotionalFamily.recentrage,
    'recentrage',
    'RECENTRAGE',
    'recentrage',
    '#7C93A8',
    'froide, contemplative',
    'silencieux, présent, sans consigne',
    'reduced',
    "Retrouver l'axe avant de reprendre la marche.",
    '« Perdre la direction n’est pas perdre le chemin. »',
    ['Écrire ce qui a changé', 'Faire l’action la plus simple', 'Ne rien décider ce soir'],
    ['editorial-cards', 'priorities'],
  ),
  [EmotionalFamily.coupDur]: family(
    EmotionalFamily.coupDur,
    'coup-dur',
    'COUP DUR',
    'coup-dur',
    '#C88A3C',
    'basse, ancrée, très contrastée',
    'solide, bref, tenant',
    'reduced',
    'Tenir, simplement.',
    '« Tenir aujourd’hui suffit. Le reste attendra demain. »',
    ['Boire, manger, dormir', 'Faire une seule chose', 'Prévenir une personne'],
    ['editorial-cards', 'priorities', 'durations'],
  ),
} satisfies Record<EmotionalFamily, EmotionalFamilyPresentationDefinition>;

export const EMOTIONAL_FAMILY_PRESENTATION_LIST: readonly EmotionalFamilyPresentationDefinition[] =
  Object.values(EMOTIONAL_FAMILY_PRESENTATIONS);

export function getFamilyPresentation(
  emotionalFamily: EmotionalFamily,
): EmotionalFamilyPresentationDefinition {
  return EMOTIONAL_FAMILY_PRESENTATIONS[emotionalFamily];
}

function state(domainState: EmotionalState, label: string): EmotionalStatePresentationDefinition {
  return {
    state: domainState,
    labelKey: `ascend.state.${domainState}.label`,
    label,
  };
}

export const EMOTIONAL_STATE_PRESENTATIONS = {
  [EmotionalState.joie]: state(EmotionalState.joie, 'JOIE'),
  [EmotionalState.accomplissement]: state(EmotionalState.accomplissement, 'ACCOMPLISSEMENT'),
  [EmotionalState.serenite]: state(EmotionalState.serenite, 'SÉRÉNITÉ'),
  [EmotionalState.sousPression]: state(EmotionalState.sousPression, 'SOUS PRESSION'),
  [EmotionalState.stress]: state(EmotionalState.stress, 'STRESS'),
  [EmotionalState.anxiete]: state(EmotionalState.anxiete, 'ANXIÉTÉ'),
  [EmotionalState.inquietude]: state(EmotionalState.inquietude, 'INQUIÉTUDE'),
  [EmotionalState.incertitude]: state(EmotionalState.incertitude, 'INCERTITUDE'),
  [EmotionalState.apprehension]: state(EmotionalState.apprehension, 'APPRÉHENSION'),
  [EmotionalState.colere]: state(EmotionalState.colere, 'COLÈRE'),
  [EmotionalState.frustration]: state(EmotionalState.frustration, 'FRUSTRATION'),
  [EmotionalState.irritation]: state(EmotionalState.irritation, 'IRRITATION'),
  [EmotionalState.impatience]: state(EmotionalState.impatience, 'IMPATIENCE'),
  [EmotionalState.injustice]: state(EmotionalState.injustice, "SENTIMENT D'INJUSTICE"),
  [EmotionalState.conflitMineur]: state(EmotionalState.conflitMineur, 'CONFLIT MINEUR'),
  [EmotionalState.solitude]: state(EmotionalState.solitude, 'SOLITUDE'),
  [EmotionalState.rejet]: state(EmotionalState.rejet, 'REJET'),
  [EmotionalState.honte]: state(EmotionalState.honte, 'HONTE'),
  [EmotionalState.culpabilite]: state(EmotionalState.culpabilite, 'CULPABILITÉ'),
  [EmotionalState.douteProfond]: state(EmotionalState.douteProfond, 'DOUTE PROFOND'),
  [EmotionalState.insuffisance]: state(EmotionalState.insuffisance, "SENTIMENT D'INSUFFISANCE"),
  [EmotionalState.malade]: state(EmotionalState.malade, 'MALADE'),
  [EmotionalState.epuise]: state(EmotionalState.epuise, 'ÉPUISÉ'),
  [EmotionalState.convalescence]: state(EmotionalState.convalescence, 'CONVALESCENCE'),
  [EmotionalState.baissePhysique]: state(EmotionalState.baissePhysique, 'BAISSE PHYSIQUE IMPORTANTE'),
  [EmotionalState.echec]: state(EmotionalState.echec, 'ÉCHEC'),
  [EmotionalState.deception]: state(EmotionalState.deception, 'DÉCEPTION'),
  [EmotionalState.perteDeReperes]: state(EmotionalState.perteDeReperes, 'PERTE DE REPÈRES'),
  [EmotionalState.confusion]: state(EmotionalState.confusion, 'CONFUSION'),
  [EmotionalState.transitionInterieure]: state(
    EmotionalState.transitionInterieure,
    'TRANSITION INTÉRIEURE',
  ),
  [EmotionalState.douteDirection]: state(EmotionalState.douteDirection, 'DOUTE SUR LA DIRECTION'),
  [EmotionalState.rupture]: state(EmotionalState.rupture, 'RUPTURE OU CONFLIT AFFECTIF MAJEUR'),
  [EmotionalState.deuil]: state(EmotionalState.deuil, 'DEUIL OU PERTE'),
  [EmotionalState.moralBas]: state(EmotionalState.moralBas, 'MORAL TRÈS BAS'),
  [EmotionalState.mauvaiseNouvelle]: state(
    EmotionalState.mauvaiseNouvelle,
    'MAUVAISE NOUVELLE PROFONDE',
  ),
  [EmotionalState.chocPersonnel]: state(EmotionalState.chocPersonnel, 'CHOC PERSONNEL IMPORTANT'),
} satisfies Record<EmotionalState, EmotionalStatePresentationDefinition>;

export const EMOTIONAL_STATE_PRESENTATION_LIST: readonly EmotionalStatePresentationDefinition[] =
  Object.values(EMOTIONAL_STATE_PRESENTATIONS);

export function getStatePresentation(
  emotionalState: EmotionalState,
): EmotionalStatePresentationDefinition {
  return EMOTIONAL_STATE_PRESENTATIONS[emotionalState];
}

/** Les définitions de présentation des états d'une famille, dans l'ordre du domaine. */
export function getStatePresentationsOfFamily(
  emotionalFamily: EmotionalFamily,
): readonly EmotionalStatePresentationDefinition[] {
  return statesOfFamily(emotionalFamily).map(getStatePresentation);
}

/**
 * Les 3 familles considérées "protection" — mêmes seuils que `--grain-opacity:.02` dans
 * `tokens/modes.css` du pack V7 (les familles les plus atténuées visuellement). Utilisé par
 * `resolvePresentation` pour mettre en avant le bloc d'intention/protection, jamais pour
 * masquer quoi que ce soit.
 */
export const PROTECTION_TIER_FAMILIES: readonly EmotionalFamily[] = [
  EmotionalFamily.vulnerabilite,
  EmotionalFamily.recentrage,
  EmotionalFamily.coupDur,
];
